import {
    Box,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useGetList, useGetMany, useGetOne } from 'react-admin';
import type { StudyForm, MemberSummary, MissionSummary } from '@wwf/sdk';
import { MemberLightCard } from '../../member/show/MemberLightCard';
import {
    computeStudyStatistics,
    type StudyAnswer,
} from './studyFormStatisticsComputation';

export const StudyFormStatisticsTab = ({
    mission,
}: {
    mission: MissionSummary;
}) => {
    const { data: answers = [], isPending } = useGetList(
        'missionStudyAnswer',
        {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'answeredAt', order: 'DESC' },
            filter: { missionId: mission.id },
            meta: {},
        },
        { enabled: !!mission.id }
    );
    const typedAnswers = answers as StudyAnswer[];

    const { perQuestion, perVolunteer } = useMemo(
        () => computeStudyStatistics(typedAnswers),
        [typedAnswers]
    );

    const { data: studyForm } = useGetOne<StudyForm>(
        'studyForm',
        { id: mission.studyFormId ?? undefined },
        { enabled: !!mission.studyFormId }
    );

    const volunteerIds = Array.from(
        new Set(typedAnswers.map(answer => answer.volunteerId).filter(Boolean))
    ) as string[];
    const { data: volunteers = [] } = useGetMany<MemberSummary>(
        'member',
        { ids: volunteerIds as MemberSummary['id'][], meta: {} },
        { enabled: volunteerIds.length > 0 }
    );
    const volunteerMap = new Map(
        volunteers.map(volunteer => [volunteer.id, volunteer])
    );

    if (isPending)
        return <Typography variant="body2">Loading statistics...</Typography>;
    if (!typedAnswers.length) {
        return (
            <Typography variant="body2">
                No study statistics available for this mission.
            </Typography>
        );
    }

    const formDefinition = studyForm?.formDefinition as
        | {
              questions?: Array<{
                  id: string;
                  title: string;
                  type: string;
                  choice1?: string;
                  choice2?: string;
                  subQuestion?: string | null;
                  images?: Array<{ key: string; name: string; image: string }>;
              }>;
          }
        | undefined;
    const questions = formDefinition?.questions ?? [];

    const questionChoicesMap = new Map<
        string,
        { choice1: string; choice2: string }
    >(
        questions
            .filter(q => q.type === 'TWO_CHOICES')
            .map(q => [
                q.id,
                {
                    choice1: q.choice1 ?? 'Choice 1',
                    choice2: q.choice2 ?? 'Choice 2',
                },
            ])
    );
    const questionImagesMap = new Map<
        string,
        Map<string, { name: string; image: string }>
    >(
        questions
            .filter(q => q.type === 'IMAGE_GALLERY')
            .map(q => [
                q.id,
                new Map(
                    (q.images ?? []).map(img => [
                        img.key,
                        { name: img.name, image: img.image },
                    ])
                ),
            ])
    );

    return (
        <Stack gap={2}>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6">Mission totals</Typography>
                    <Typography variant="body2">
                        Total touched persons:{' '}
                        {mission.totalTouchedPersonCount ?? 0}
                    </Typography>
                    <Typography variant="body2">
                        Total mascot hugs: {mission.totalMascotHugCount ?? 0}
                    </Typography>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6">Per question</Typography>
                    <Stack gap={2} mt={1}>
                        {questions.map(q => {
                            const stats = perQuestion.get(q.id);
                            if (!stats) return null;

                            const choices = questionChoicesMap.get(q.id);
                            const imageInfo = questionImagesMap.get(q.id);

                            return (
                                <Box key={q.id}>
                                    <Typography variant="subtitle2">
                                        {q.title} ({q.type})
                                    </Typography>
                                    <Typography variant="body2">
                                        Answer rows: {stats.answerRows} -
                                        Touched persons: {stats.touchedPeople}
                                    </Typography>
                                    {q.type === 'TWO_CHOICES' && (
                                        <Typography
                                            variant="caption"
                                            display="block"
                                        >
                                            {choices?.choice1 ?? 'Choice 1'}:{' '}
                                            {stats.stats.choice1} -{' '}
                                            {choices?.choice2 ?? 'Choice 2'}:{' '}
                                            {stats.stats.choice2}
                                        </Typography>
                                    )}
                                    {q.type === 'YES_NO' && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                            >
                                                Yes: {stats.stats.yes} - No:{' '}
                                                {stats.stats.no}
                                            </Typography>
                                            {q.subQuestion && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        ml: 1,
                                                    }}
                                                >
                                                    {q.subQuestion} {'->'} Yes:{' '}
                                                    {stats.stats.subYes} - No:{' '}
                                                    {stats.stats.subNo}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {q.type === 'IMAGE_GALLERY' && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                fontWeight="bold"
                                            >
                                                Selections per image:
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                flexWrap="wrap"
                                                gap={1}
                                            >
                                                {Array.from(
                                                    stats.stats.images as Map<
                                                        string,
                                                        number
                                                    >
                                                ).map(([imgKey, count]) => {
                                                    const info =
                                                        imageInfo?.get(imgKey);
                                                    return (
                                                        <Stack
                                                            key={imgKey}
                                                            direction="row"
                                                            alignItems="center"
                                                            gap={0.5}
                                                            sx={{
                                                                backgroundColor:
                                                                    'action.hover',
                                                                px: 0.5,
                                                                py: 0.25,
                                                                borderRadius: 1,
                                                                border: theme =>
                                                                    `1px solid ${theme.palette.divider}`,
                                                            }}
                                                        >
                                                            {info?.image && (
                                                                <Box
                                                                    component="img"
                                                                    src={
                                                                        info.image
                                                                    }
                                                                    alt={
                                                                        info.name
                                                                    }
                                                                    sx={{
                                                                        width: 24,
                                                                        height: 16,
                                                                        objectFit:
                                                                            'cover',
                                                                        borderRadius: 0.25,
                                                                    }}
                                                                />
                                                            )}
                                                            <Typography variant="caption">
                                                                {info?.name ||
                                                                    imgKey}
                                                                : {count}
                                                            </Typography>
                                                        </Stack>
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    )}
                                    <Divider sx={{ mt: 1 }} />
                                </Box>
                            );
                        })}
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6">Per volunteer</Typography>
                    <Stack gap={1} mt={1}>
                        {Array.from(perVolunteer.entries()).map(
                            ([volunteerId, stats]) => {
                                const volunteer = volunteerMap.get(
                                    volunteerId as MemberSummary['id']
                                );
                                return (
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="center"
                                        gap={2}
                                        key={volunteerId}
                                    >
                                        {volunteer ? (
                                            <MemberLightCard
                                                record={volunteer}
                                            />
                                        ) : (
                                            <Typography variant="subtitle2">
                                                {volunteerId}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" mt={0.5}>
                                            Form submissions:{' '}
                                            {stats.submissions} - Touched
                                            persons: {stats.touchedPeople} -
                                            Mascot hugs: {stats.mascotHugs}
                                        </Typography>
                                        <Divider sx={{ mt: 1 }} />
                                    </Box>
                                );
                            }
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
};
